import { MAZE_BALL_SIZE, MAZE_CELL_SIZE } from '@/constants/maze';
import type { MazeStagePixiPalette } from '@/theme/maze-stage-palette';
import * as PIXI from 'pixi.js';

export function createMazeBeaconPixi(
  goalX: number,
  goalY: number,
  palette: MazeStagePixiPalette,
): PIXI.Container {
  const beacon = new PIXI.Container();
  beacon.x = goalX * MAZE_CELL_SIZE + MAZE_CELL_SIZE / 2;
  beacon.y = goalY * MAZE_CELL_SIZE + MAZE_CELL_SIZE / 2;
  const glow = new PIXI.Graphics();
  glow.beginFill(palette.beaconOuter, 0.26);
  // Nearly cell-wide glow; outer stroke ring sits just inside the cell edges.
  glow.drawCircle(0, 0, MAZE_CELL_SIZE * 0.49);
  glow.endFill();
  beacon.addChild(glow);

  // Outer ring ~flush with corridor walls (cell is MAZE_CELL_SIZE square, center = half cell).
  const ringRadii = [MAZE_CELL_SIZE * 0.47, MAZE_CELL_SIZE * 0.29, MAZE_CELL_SIZE * 0.14];
  const ringStrokes = [1.8, 1.2, 0.8];
  for (let index = 0; index < ringRadii.length; index += 1) {
    const radius = ringRadii[index];
    const ring = new PIXI.Graphics();
    ring.lineStyle(ringStrokes[index], palette.beaconInner, 0.96);
    ring.drawCircle(0, 0, radius);
    beacon.addChild(ring);
  }

  return beacon;
}

export interface MazePlayerPixiParts {
  player: PIXI.Container;
}

export function createMazePlayerPixi(
  startCol: number,
  startRow: number,
  palette: MazeStagePixiPalette,
): MazePlayerPixiParts {
  const player = new PIXI.Container();
  player.x = startCol * MAZE_CELL_SIZE + MAZE_CELL_SIZE / 2;
  player.y = startRow * MAZE_CELL_SIZE + MAZE_CELL_SIZE / 2;

  const playerMask = new PIXI.Graphics();
  playerMask.beginFill(palette.playerMask);
  playerMask.drawCircle(0, 0, MAZE_BALL_SIZE);
  playerMask.endFill();
  playerMask.renderable = false;

  const yellow = palette.playerBase;

  const ballOuterGlow = new PIXI.Graphics();
  ballOuterGlow.beginFill(palette.playerShadow, 0.14);
  ballOuterGlow.drawCircle(0, 0, MAZE_BALL_SIZE * 1.52);
  ballOuterGlow.endFill();
  ballOuterGlow.beginFill(yellow, 0.1);
  ballOuterGlow.drawCircle(0, 0, MAZE_BALL_SIZE * 1.28);
  ballOuterGlow.endFill();
  ballOuterGlow.lineStyle(3, yellow, 0.22, 1);
  ballOuterGlow.drawCircle(0, 0, MAZE_BALL_SIZE + 1.4);
  ballOuterGlow.lineStyle(2, palette.playerShadow, 0.18, 1);
  ballOuterGlow.drawCircle(0, 0, MAZE_BALL_SIZE + 0.55);
  ballOuterGlow.lineStyle(0);

  const playerBody = new PIXI.Graphics();
  playerBody.beginFill(palette.playerBase);
  playerBody.drawCircle(0, 0, MAZE_BALL_SIZE);
  playerBody.endFill();
  playerBody.beginFill(palette.playerBase, 0.36);
  playerBody.drawCircle(2.6, 3.1, MAZE_BALL_SIZE * 0.86);
  playerBody.endFill();
  playerBody.beginFill(palette.playerCore, 0.96);
  playerBody.drawCircle(-3.2, -3.6, MAZE_BALL_SIZE * 0.56);
  playerBody.endFill();
  playerBody.beginFill(palette.playerCore, 0.22);
  playerBody.drawCircle(-1.1, -1.2, MAZE_BALL_SIZE * 0.82);
  playerBody.endFill();
  playerBody.lineStyle(1.5, palette.playerSpecular, 0.18);
  playerBody.drawCircle(0, 0, MAZE_BALL_SIZE * 0.95);
  playerBody.lineStyle(0);

  const ballInnerGlow = new PIXI.Graphics();
  ballInnerGlow.beginFill(palette.playerShadow, 0.13);
  ballInnerGlow.drawEllipse(2.2, 2.4, MAZE_BALL_SIZE * 0.74, MAZE_BALL_SIZE * 0.58);
  ballInnerGlow.endFill();
  ballInnerGlow.beginFill(yellow, 0.1);
  ballInnerGlow.drawEllipse(-1.6, -1.8, MAZE_BALL_SIZE * 0.38, MAZE_BALL_SIZE * 0.34);
  ballInnerGlow.endFill();
  ballInnerGlow.lineStyle(2, palette.playerShadow, 0.14, 0);
  ballInnerGlow.drawCircle(0, 0, MAZE_BALL_SIZE - 1.2);
  ballInnerGlow.lineStyle(1.2, yellow, 0.16, 0);
  ballInnerGlow.drawCircle(0, 0, MAZE_BALL_SIZE - 2.1);
  ballInnerGlow.lineStyle(0);
  ballInnerGlow.mask = playerMask;

  const playerShade = new PIXI.Graphics();
  playerShade.beginFill(palette.playerBase, 0.28);
  playerShade.drawEllipse(
    MAZE_BALL_SIZE * 0.24,
    MAZE_BALL_SIZE * 0.2,
    MAZE_BALL_SIZE * 0.92,
    MAZE_BALL_SIZE * 0.62,
  );
  playerShade.endFill();
  playerShade.mask = playerMask;

  player.addChild(ballOuterGlow);
  player.addChild(playerBody);
  player.addChild(ballInnerGlow);
  player.addChild(playerShade);
  player.addChild(playerMask);

  return { player };
}
